import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, CircularProgress, Stack, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert, Chip } from '@mui/material';
import { fetchAuthSession } from 'aws-amplify/auth';
import { updatePassword } from 'aws-amplify/auth';

function ProfileField({ label, value }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="body1">{value || '—'}</Typography>
    </Box>
  );
}

export default function Profile() {
  const { t } = useTranslation();
  const [user, setUser] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [passwords, setPasswords] = useState({ current: '', next: '', confirm: '' });
  const [changing, setChanging] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    fetchAuthSession().then(session => {
      const p = session.tokens?.idToken?.payload ?? {};
      setUser({
        email: p.email,
        name: p.name,
        company: p['custom:company'],
        companyPath: p['custom:company_path'],
        groups: (() => { const g = p['cognito:groups']; return Array.isArray(g) ? g : g ? g.replace(/^\[|\]$/g, '').split(/\s+/).filter(Boolean) : []; })(),
      });
    });
  }, []);

  const handleOpen = () => {
    setPasswords({ current: '', next: '', confirm: '' });
    setError('');
    setSuccess(false);
    setDialogOpen(true);
  };

  const handleClose = () => {
    if (!changing) setDialogOpen(false);
  };

  const handleChange = (e) => {
    setPasswords(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async () => {
    if (passwords.next !== passwords.confirm) {
      setError(t('profile.passwordMismatch'));
      return;
    }
    setChanging(true);
    setError('');
    try {
      await updatePassword({ oldPassword: passwords.current, newPassword: passwords.next });
      setSuccess(true);
      setChanging(false);
    } catch (e) {
      setError(e.message || t('profile.failedToChangePassword'));
      setChanging(false);
    }
  };

  return (
    <Box>
      <Typography variant='h2' sx={{ color: 'primary.main', textAlign: 'left' }}>{t('profile.title')}</Typography>
      {!user ? (
        <CircularProgress sx={{ mt: 2 }} />
      ) : (
        <Stack spacing={2} sx={{ mt: 3, maxWidth: 480 }}>
          <ProfileField label={t('profile.email')} value={user.email} />
          <ProfileField label={t('profile.name')} value={user.name} />
          <ProfileField label={t('profile.company')} value={user.company} />
          <ProfileField label={t('profile.companyPath')} value={user.companyPath} />
          <Box>
            <Typography variant="caption" color="text.secondary">{t('profile.groups')}</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
              {user.groups.length === 0
                ? <Typography variant="body1">—</Typography>
                : user.groups.map(g => <Chip key={g} label={g} size="small" />)
              }
            </Box>
          </Box>
          <Box>
            <Button variant="outlined" onClick={handleOpen}>{t('profile.changePassword')}</Button>
          </Box>
        </Stack>
      )}

      <Dialog open={dialogOpen} onClose={handleClose} fullWidth maxWidth="xs">
        <DialogTitle>{t('profile.changePasswordTitle')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {success ? (
              <Alert severity="success">{t('profile.passwordChangedSuccess')}</Alert>
            ) : (
              <>
                {error && <Alert severity="error">{error}</Alert>}
                <TextField
                  label={t('profile.currentPassword')}
                  name="current"
                  type="password"
                  value={passwords.current}
                  onChange={handleChange}
                  fullWidth
                  autoComplete="current-password"
                />
                <TextField
                  label={t('profile.newPassword')}
                  name="next"
                  type="password"
                  value={passwords.next}
                  onChange={handleChange}
                  fullWidth
                  autoComplete="new-password"
                />
                <TextField
                  label={t('profile.confirmNewPassword')}
                  name="confirm"
                  type="password"
                  value={passwords.confirm}
                  onChange={handleChange}
                  fullWidth
                  autoComplete="new-password"
                />
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={changing}>{t('common.cancel')}</Button>
          {!success && (
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={changing || !passwords.current || !passwords.next || !passwords.confirm}
            >
              {changing ? <CircularProgress size={20} /> : t('profile.changePassword')}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
